import { Fiat, Offer, Wallet } from "@models";
import { Tradable } from "@models/interfaces/tradable";
import { Directive, HostListener, Input } from "@angular/core";
import { ModalService } from "@core-services/modal.service";
import { StockTestService } from "@main-services/stock-test.service";
import { matchesMediaQuery } from "@helpers/utils";
import { AccountService } from "../account/services/account.service";
import { TooltipCoinOptions, TooltipService } from "@main-services/tooltip.service";
import { UserService } from "@main-services/user.service";
import { StockTestHelperService } from "@main-services/stock-test-helper.service";
import { TRADE_ACTION } from "./action-buttons.config";
import { SMALL_DEVICES_WIDTH_THRESHOLD } from "@helpers/constants";
import { LaunchDarklyService, LD_FLAG } from "@core-services/launch-darkly/launch-darkly.service";
import { take } from "rxjs/operators";
import { FiatWalletService } from "@main-services/wallet/fiat_wallet.service";
import { TRADE_TYPE } from "@models/trade-offer.model";
import { CheckoutEntryHelperService } from "@modules/checkout/services/checkout-entry-helper.service";

@Directive()
export abstract class ActionButtonsComponent<W extends Wallet> {
	@Input()
	public alternateFiat: Fiat = null;

	@Input() public wallet: W = null;
	public buttonActions: string[] = [];
	public mobileWindow: boolean = false;
	protected joinedActionButtonClicked: boolean = false;

	protected constructor(
		protected accountService: AccountService,
		protected modalService: ModalService,
		protected userService: UserService,
		protected tooltipService: TooltipService,
		protected stockTestService: StockTestService,
		protected stockTestHelperService: StockTestHelperService,
		protected readonly launchDarklyService: LaunchDarklyService,
		protected readonly fiatWalletService: FiatWalletService,
		protected readonly checkoutEntryHelper: CheckoutEntryHelperService
	) {}

	public get fiat(): Fiat {
		return this.alternateFiat || this.accountService.getDefaultFiat() || new Fiat();
	}

	public abstract isActionButtonDisabled(s: string, tradable: Tradable): boolean;

	public abstract onActionClick(action: string, wallet: W): void;

	public abstract getTooltip(action: string, tradable: Tradable): string;

	// For wrapper components in mobile view
	public mainButtonToggle(): boolean {
		this.joinedActionButtonClicked = !this.joinedActionButtonClicked;
		return this.isJoinedActionButtonClicked();
	}

	public isMobileWindow(): boolean {
		return this.mobileWindow;
	}

	@HostListener("window: resize")
	public onResize(): void {
		this.mobileWindow = !matchesMediaQuery(SMALL_DEVICES_WIDTH_THRESHOLD);
	}

	public isJoinedActionButtonClicked(): boolean {
		return this.joinedActionButtonClicked;
	}

	protected getTooltipString(options: TooltipCoinOptions): string {
		if (!this.isActionButtonDisabled(options.action, options.tradable || null)) {
			return "";
		}

		return this.tooltipService.getTooltip(options);
	}

	protected isVerificationMandatory(): boolean {
		return this.userService.isVerificationMandatory;
	}

	protected tradeAction(action: string, offer: Offer): void {
		this.launchDarklyService
			.isActive$(LD_FLAG.CHECKOUT_FLOW_ENABLED)
			.pipe(take(1))
			.subscribe({
				next: (flag) => {
					if (flag && action === TRADE_TYPE.BUY) {
						// New CHECKOUT BUY flow
						this.modalService.pop();
						const assetId = offer.getCurrencyId();
						this.checkoutEntryHelper.onTradableSelect(assetId);
					} else {
						// Legacy BUY flow
						const tradeAction = TRADE_ACTION[action];
						offer.setType(tradeAction.type);

						const showModal: () => void = () =>
							this.modalService.create(tradeAction.component, { data: offer }).present();

						this.stockTestService.needsTest(offer.getCurrencyId())
							? this.stockTestHelperService.startTest(showModal)
							: showModal();
					}
				}
			});
	}
}
